import { forwardRef } from "react"
import { DailyVideo, useNetwork, useParticipantProperty } from "@daily-co/daily-react"
import { Hand, Mic, MicOff, Pin, PinOff, WifiOff } from "lucide-react"
import { AvatarBubble } from "../shared/AvatarBubble"
import { useRaisedHandsContext } from "./useRaisedHands"
import { useSpeakingState } from "./useSpeakingState"

export type VideoTileVariant = "stage" | "grid" | "thumb"
export type VideoTileMicState = "muted" | "quiet" | "speaking"

type VideoTileProps = {
  sessionId: string
  variant: VideoTileVariant
  isLocal?: boolean
  isActiveSpeaker?: boolean
  isPinned?: boolean
  /** When provided, the tile shows a pin control (on hover/focus, always while pinned). */
  onTogglePin?: () => void
  className?: string
}

const variantClasses: Record<VideoTileVariant, string> = {
  stage: "h-full w-full rounded-2xl",
  grid: "aspect-video w-full rounded-2xl",
  thumb: "aspect-video w-full rounded-xl",
}

export const VideoTile = forwardRef<HTMLVideoElement, VideoTileProps>(function VideoTile(
  { sessionId, variant, isLocal = false, isActiveSpeaker = false, isPinned = false, onTogglePin, className },
  ref,
) {
  const userName = useParticipantProperty(sessionId, "user_name")
  const isOwner = useParticipantProperty(sessionId, "owner")
  const audioOn = useParticipantProperty(sessionId, "audio")
  const videoOn = useParticipantProperty(sessionId, "video")
  const videoState = useParticipantProperty(sessionId, "tracks.video.state")
  const videoOff = useParticipantProperty(sessionId, "tracks.video.off")
  const network = useNetwork()
  const handPosition = useRaisedHandsContext().positionOf(sessionId)
  const { speaking, level } = useSpeakingState(sessionId)
  // Muted, quiet, or speaking: the level observer with Daily's active-speaker signal behind it, on every tile.
  const micState: VideoTileMicState = !audioOn ? "muted" : speaking || isActiveSpeaker ? "speaking" : "quiet"
  // Three-bar meter driven by the live level (like Meet's), with a floor so the badge never looks empty while speaking.
  const meterLevel = Math.max(level, micState === "speaking" ? 0.2 : 0)
  const meterBars = [0.7, 1, 0.85].map((weight) => `${Math.round(20 + Math.min(1, meterLevel * weight) * 80)}%`)

  const displayName = (userName as string | undefined)?.trim() || (isLocal ? "You" : "Guest")
  const showVideo = Boolean(videoOn) && (videoState === "playable" || videoState === "loading" || videoState === "sendable")
  const videoInterrupted = videoState === "interrupted" && !videoOff?.byUser
  const connectionIssue = videoInterrupted || (isLocal && network.threshold === "very-low")
  const nameSize = variant === "thumb" ? "text-[11px]" : "text-xs"
  // Only a pinned tile gets an outline (its own layer above the video, so it is never hidden behind it);
  // who is speaking is shown by the microphone badge alone, as in Google Meet.
  const outline = isPinned ? "ring-sky-300" : null

  return (
    <div
      data-testid={`call-tile-${sessionId}`}
      data-variant={variant}
      data-active-speaker={micState === "speaking" ? "true" : undefined}
      data-pinned={isPinned ? "true" : undefined}
      className={`group relative overflow-hidden bg-[#1c2530] ring-1 ring-inset ring-white/5 ${variantClasses[variant]} ${className || ""}`}
    >
      {outline && <span aria-hidden="true" data-testid={`call-outline-${sessionId}`} className={`pointer-events-none absolute inset-0 z-10 rounded-[inherit] ring-2 ring-inset ${outline}`} />}
      {showVideo ? (
        <DailyVideo
          ref={ref}
          sessionId={sessionId}
          type="video"
          automirror
          fit={variant === "stage" ? "contain" : "cover"}
          playsInline
          className="h-full w-full bg-black object-cover"
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-[#1c2530]">
          <AvatarBubble
            name={displayName}
            seed={sessionId}
            className={variant === "thumb" ? "h-10 w-10 text-sm" : "h-20 w-20 text-2xl"}
          />
        </div>
      )}
      {handPosition !== null && (
        <span
          data-testid={`call-tile-hand-${sessionId}`}
          role="img"
          aria-label={`${displayName} raised their hand`}
          className={`absolute left-2 top-2 z-10 flex items-center gap-1 rounded-full bg-black/65 font-semibold text-white ${variant === "thumb" ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-1 text-xs"}`}
        >
          <Hand className={variant === "thumb" ? "h-3 w-3" : "h-3.5 w-3.5"} />
          {handPosition}
        </span>
      )}
      {(
        <span
          data-testid={`call-mic-${sessionId}`}
          data-state={micState}
          role="img"
          aria-label={micState === "muted" ? `${displayName} is muted` : micState === "speaking" ? `${displayName} is speaking` : `${displayName} is not speaking`}
          className={`absolute right-2 top-2 z-10 flex items-center justify-center rounded-full ${micState === "speaking" ? "bg-[#f6a35c] text-[#1c2530]" : "bg-black/60 text-white"} ${variant === "thumb" ? "h-5 w-5" : "h-7 w-7"}`}
        >
          {micState === "muted" ? (
            <MicOff className={variant === "thumb" ? "h-3 w-3" : "h-3.5 w-3.5"} />
          ) : micState === "speaking" ? (
            <span className="flex h-3 items-end gap-[2px]" aria-hidden="true" data-testid={`call-mic-level-${sessionId}`} data-level={level}>
              {meterBars.map((height, index) => (
                <span key={index} className="w-[3px] rounded-full bg-current transition-[height] duration-100 ease-out" style={{ height }} />
              ))}
            </span>
          ) : (
            <Mic className={variant === "thumb" ? "h-3 w-3" : "h-3.5 w-3.5"} />
          )}
        </span>
      )}
      {onTogglePin && (
        <button
          type="button"
          data-testid={`call-tile-pin-${sessionId}`}
          aria-label={`${isPinned ? "Unpin" : "Pin"} ${displayName}`}
          aria-pressed={isPinned}
          onClick={(event) => {
            event.stopPropagation()
            onTogglePin()
          }}
          className={`absolute top-2 z-10 rounded-full bg-black/60 p-1.5 text-white transition hover:bg-black/80 focus-visible:opacity-100 ${variant === "thumb" ? "right-8" : "right-11"} ${isPinned ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
        >
          {isPinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
        </button>
      )}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-between gap-2 bg-gradient-to-t from-black/70 to-transparent px-2.5 pb-2 pt-6">
        <span className={`flex min-w-0 items-center gap-1.5 ${nameSize} font-medium text-white`}>
          <span className="truncate">{displayName}{isLocal ? " (you)" : ""}</span>
          {isOwner && <span className="shrink-0 rounded-full bg-white/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide">Host</span>}
          {isPinned && <span className="shrink-0 rounded-full bg-sky-400/25 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-sky-100">Pinned</span>}
        </span>
        {connectionIssue && (
          <span className="flex shrink-0 items-center gap-1 rounded-full bg-black/60 px-2 py-0.5 text-[10px] font-semibold text-amber-200">
            <WifiOff className="h-3 w-3" />
            {videoInterrupted ? "Camera interrupted" : "Weak connection"}
          </span>
        )}
      </div>
    </div>
  )
})
